'use client';

import { CodeEntry, contactDelivery, ReturnToStep } from './CodeStepScreen';
import { useSignupApi } from '../hooks/useSignupApi';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { SIGNUP_LOGIN_PAGE } from '../off-path-pages';
import { isContactOnAnotherAccount } from '../services/contact-on-another-account';
import { loginDestination } from '../services/login-resume';
import { SignupRequestError } from '../services/signup-api/signup-api';
import { useSignupFlowStore, type SignupSignInContact } from '../stores/signup-flow-store';

/**
 * Verify answers meaning no account sits behind the contact. Galapagos only
 * says so at verify: the code request answers the same for every contact.
 */
const NO_ACCOUNT_CODES = new Set(['account_not_found', 'terms_required']);

function LoginCodeStep({ contact }: { contact: SignupSignInContact }) {
  const api = useSignupApi();
  const { goToStep } = useSignupNavigation();
  const setLoginNotice = useSignupFlowStore((state) => state.setLoginNotice);
  const setLoginProfileState = useSignupFlowStore((state) => state.setLoginProfileState);

  // The registration profile alone decides where a returning member resumes.
  // A failed read has nothing to resume from either.
  const resume = async (stillCurrent: () => boolean) => {
    const profile = await api.getRegistrationProfile().catch(() => null);
    if (!stillCurrent()) return;
    // The welcome-back page reuses this read rather than asking again.
    setLoginProfileState(profile === null ? null : profile.completedAt ? 'held' : 'unsaved');
    goToStep(loginDestination(profile));
  };

  const onRejected = (error: unknown) => {
    if (isContactOnAnotherAccount(error)) {
      setLoginNotice('contact-on-another-account');
      goToStep(SIGNUP_LOGIN_PAGE);
      return true;
    }
    if (
      !(error instanceof SignupRequestError) ||
      error.code === undefined ||
      !NO_ACCOUNT_CODES.has(error.code)
    ) {
      return false;
    }
    setLoginNotice('account-not-found');
    goToStep(SIGNUP_LOGIN_PAGE);
    return true;
  };

  // A sign-in sends no consent: the member gave it when the account was made.
  return (
    <CodeEntry delivery={contactDelivery(contact)} afterSignIn={resume} onRejected={onRejected} />
  );
}

/** `/login-code/`: checks the code the login page sent, then resumes (v9 4.2). */
export function LoginCodeStepScreen() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const contact = useSignupFlowStore((state) => state.signInContact);
  return contact ? <LoginCodeStep contact={contact} /> : <ReturnToStep page={SIGNUP_LOGIN_PAGE} />;
}
